import { useEffect, useState } from "react";
import { createLead, fetchAvailability, bookAppointment, requestCallback } from "../../lib/api";
import { toast } from "sonner";
import { X, ArrowLeft, ArrowRight, Upload, Sparkles, CheckCircle2, MapPin, Sun, Camera, User, Calendar as CalIcon, Phone } from "lucide-react";

const STEPS = ["Service", "Address", "Roof", "Solar", "Photos", "Contact", "Estimate", "Schedule", "Done"];

export default function LeadWizard({ onClose }) {
  const [step, setStep] = useState(0);
  const [data, setData] = useState({
    service_type: "replacement",
    address: "",
    city: "",
    zip_code: "",
    roof_age: "",
    roof_type: "",
    shingle_type: "standard",
    roof_pitch: "le_7",
    square_footage: "",
    has_solar: false,
    solar_panel_count: "",
    damage_notes: "",
    stories: "1",
    gutter_lf: "",
    downspout_count: "",
    photos: [],
    full_name: "",
    email: "",
    phone: "",
  });
  const [lead, setLead] = useState(null);
  const [loadingEst, setLoadingEst] = useState(false);
  const [availability, setAvailability] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [booking, setBooking] = useState(false);
  const [callingBack, setCallingBack] = useState(false);
  const [callbackDone, setCallbackDone] = useState(false);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const next = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const canNext = () => {
    if (step === 0) return !!data.service_type;
    if (step === 1) return data.address.trim().length > 3;
    if (step === 5) return data.full_name && data.email && data.phone;
    return true;
  };

  const handlePhotos = async (files) => {
    const arr = Array.from(files).slice(0, 5);
    const out = [];
    for (const f of arr) {
      if (f.size > 2_500_000) {
        toast.error(`${f.name} too large (max ~2.5MB)`);
        continue;
      }
      const b64 = await new Promise((res) => {
        const r = new FileReader();
        r.onload = () => res(r.result);
        r.readAsDataURL(f);
      });
      out.push(b64);
    }
    set("photos", [...(data.photos || []), ...out].slice(0, 5));
  };

  // When moving INTO Estimate step → submit lead
  useEffect(() => {
    if (step === 6 && !lead) {
      submitLead();
    }
    if (step === 7 && availability.length === 0) {
      loadAvailability();
    }
    // eslint-disable-next-line
  }, [step]);

  const submitLead = async () => {
    setLoadingEst(true);
    try {
      const created = await createLead(data);
      setLead(created);
    } catch (err) {
      toast.error("Could not generate estimate. Please try again.");
      setStep(4);
    } finally {
      setLoadingEst(false);
    }
  };

  const loadAvailability = async () => {
    try {
      const res = await fetchAvailability();
      setAvailability(res.days || []);
    } catch {
      toast.error("Could not load availability");
    }
  };

  const book = async () => {
    if (!selectedDate || !selectedSlot) return toast.error("Pick a date and time");
    setBooking(true);
    try {
      const booked = await bookAppointment({ lead_id: lead.id, appointment_date: selectedDate, appointment_slot: selectedSlot });
      setLead((l) => ({ ...l, appointment_date: selectedDate, appointment_slot: selectedSlot, ...booked }));
      setStep(8);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Booking failed");
    } finally {
      setBooking(false);
    }
  };

  const callMeInstead = async () => {
    if (!lead) return;
    setCallingBack(true);
    try {
      await requestCallback(lead.id);
      setCallbackDone(true);
      toast.success("Got it — we'll call you shortly.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not request a callback");
    } finally {
      setCallingBack(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-stretch md:items-center justify-center md:p-6" data-testid="wizard-modal">
      <div className="bg-white w-full md:max-w-3xl md:rounded-2xl shadow-2xl flex flex-col max-h-screen md:max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center">
              <Sparkles className="w-4 h-4" />
            </div>
            <div>
              <div className="font-display text-lg font-semibold tracking-tight">Get your instant bid</div>
              <div className="text-xs text-slate-500">Step {Math.min(step + 1, STEPS.length)} of {STEPS.length} · {STEPS[step]}</div>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100" data-testid="wizard-close-button">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Progress */}
        <div className="px-6 pt-3">
          <div className="flex gap-1">
            {STEPS.map((s, i) => (
              <div key={s} className={`h-1.5 flex-1 rounded-full ${i <= step ? "bg-amber-500" : "bg-slate-200"}`} />
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          {step === 0 && (
            <StepWrap icon={<Sparkles className="w-5 h-5" />} title="What do you need?" subtitle="Pick the service that fits — we'll tailor the rest of the form.">
              <div className="space-y-3" data-testid="service-picker">
                <ServiceChoice
                  active={data.service_type === "replacement"}
                  onClick={() => set("service_type", "replacement")}
                  title="Full Roof Replacement"
                  body="Full re-roof: tear-off, felt, shingles, and accessories. If you have solar, we detach and reset the panels in-house."
                  testId="service-replacement"
                />
                <ServiceChoice
                  active={data.service_type === "repair"}
                  onClick={() => set("service_type", "repair")}
                  title="Roof Repair"
                  body="Damaged shingles, flashings, pipe boots, vents, ridge vents, turbines. Transparent menu pricing."
                  testId="service-repair"
                />
                <ServiceChoice
                  active={data.service_type === "solar_repair"}
                  onClick={() => set("service_type", "solar_repair")}
                  title="Solar Panel Repair"
                  body="Panel reseat, wiring, inverter issues. We're a TDLR-licensed electrical contractor — one of the few in DFW who'll take the call."
                  testId="service-solar-repair"
                  highlight
                />
                <ServiceChoice
                  active={data.service_type === "gutters"}
                  onClick={() => set("service_type", "gutters")}
                  title="Seamless Gutters"
                  body="New seamless gutters & downspouts at $8.75/linear foot. Know your footage? Get an instant price. Two-story homes we measure on site."
                  testId="service-gutters"
                />
              </div>
            </StepWrap>
          )}

          {step === 1 && (
            <StepWrap icon={<MapPin className="w-5 h-5" />} title="What&apos;s the property address?" subtitle="DFW area only for our pilot — Fort Worth, Dallas, Arlington, Plano, McKinney.">
              <Input label="Street address" value={data.address} onChange={(v) => set("address", v)} placeholder="123 Magnolia Ave" testId="address-input" />
              <div className="grid grid-cols-2 gap-4 mt-4">
                <Input label="City" value={data.city} onChange={(v) => set("city", v)} placeholder="Fort Worth" testId="city-input" />
                <Input label="Zip code" value={data.zip_code} onChange={(v) => set("zip_code", v)} placeholder="76107" testId="zip-input" />
              </div>
            </StepWrap>
          )}

          {step === 2 && (
            data.service_type === "gutters" ? (
              <StepWrap icon={<MapPin className="w-5 h-5" />} title="Tell us about your gutters" subtitle="Seamless gutters & downspouts are $8.75 per linear foot installed.">
                <RadioGrid label="How many stories?" value={data.stories} onChange={(v) => set("stories", v)} options={["1", "2+"]} testIdPrefix="stories" />
                <Input label="Approx. linear feet of gutter" type="number" value={data.gutter_lf} onChange={(v) => set("gutter_lf", v)} placeholder="e.g. 180" testId="gutter-lf-input" />
                <div className="mt-4">
                  <Input label="Number of downspouts (optional)" type="number" value={data.downspout_count} onChange={(v) => set("downspout_count", v)} placeholder="e.g. 4" testId="downspout-count-input" />
                </div>
                <p className="text-xs text-slate-500 mt-3">Not sure of your footage? Leave it blank — we'll measure on site and price it at $8.75/ft.</p>
              </StepWrap>
            ) : (
              <StepWrap icon={<MapPin className="w-5 h-5" />} title="Tell us about your roof" subtitle="A few quick details so we can price your re-roof accurately.">
                <RadioGrid label="Shingle type" value={data.shingle_type} onChange={(v) => set("shingle_type", v)} options={[{ value: "standard", label: "Standard architectural" }, { value: "class4", label: "Class 4 impact-resistant" }]} testIdPrefix="shingle-type" />
                <RadioGrid label="Roof pitch (steepness)" value={data.roof_pitch} onChange={(v) => set("roof_pitch", v)} options={[{ value: "le_7", label: "Up to 7/12" }, { value: "8_10", label: "8/12–10/12" }, { value: "11_12", label: "11/12–12/12" }, { value: "over_12", label: "Over 12/12" }]} testIdPrefix="pitch" />
                <RadioGrid label="Roof age (years)" value={data.roof_age} onChange={(v) => set("roof_age", v)} options={["0-5", "5-10", "10-20", "20+"]} testIdPrefix="roof-age" />
                <RadioGrid label="Home square footage" value={data.square_footage} onChange={(v) => set("square_footage", v)} options={["1000-1500", "1500-2000", "2000-2500", "2500-3500", "3500+"]} testIdPrefix="sqft" />
              </StepWrap>
            )
          )}

          {step === 3 && (
            <StepWrap icon={<Sun className="w-5 h-5" />} title={data.service_type === "gutters" ? "Anything else?" : "Solar panels?"} subtitle={data.service_type === "gutters" ? "Add any details that help us bid your gutters accurately." : "We specialize in roofs with existing solar — we'll remove and reset them as part of the project."}>
              {data.service_type !== "gutters" && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <ToggleCard active={data.has_solar === true} onClick={() => set("has_solar", true)} label="Yes, I have solar" testId="solar-yes" />
                    <ToggleCard active={data.has_solar === false} onClick={() => { set("has_solar", false); set("solar_panel_count", ""); }} label="No solar panels" testId="solar-no" />
                  </div>
                  {data.has_solar && (
                    <div className="mt-5">
                      <RadioGrid label="How many panels?" value={data.solar_panel_count} onChange={(v) => set("solar_panel_count", v)} options={["1-10", "10-20", "20-30", "30+"]} testIdPrefix="panel-count" />
                    </div>
                  )}
                </>
              )}
              <div className="mt-5">
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Damage or specific notes (optional)</label>
                <textarea
                  value={data.damage_notes}
                  onChange={(e) => set("damage_notes", e.target.value)}
                  rows={3}
                  placeholder="e.g. leaking near chimney, hail last spring, missing shingles on north side"
                  className="mt-1 w-full px-4 py-3 rounded-lg border border-slate-200 focus:ring-2 focus:ring-amber-500 focus:outline-none"
                  data-testid="damage-notes-input"
                />
              </div>
            </StepWrap>
          )}

          {step === 4 && (
            <StepWrap icon={<Camera className="w-5 h-5" />} title="Add a few photos (optional)" subtitle="Up to 5 photos — closer the better. Helps us calibrate the bid.">
              <label className="block border-2 border-dashed border-slate-300 rounded-xl p-10 text-center cursor-pointer hover:border-amber-500 transition" data-testid="photo-dropzone">
                <Upload className="w-8 h-8 text-slate-400 mx-auto mb-3" />
                <div className="font-medium text-slate-700">Tap to upload roof photos</div>
                <div className="text-xs text-slate-500 mt-1">PNG/JPG · up to 2.5MB each · max 5</div>
                <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => handlePhotos(e.target.files)} data-testid="photo-input" />
              </label>
              {data.photos.length > 0 && (
                <div className="grid grid-cols-5 gap-2 mt-4" data-testid="photo-grid">
                  {data.photos.map((p, i) => (
                    <div key={i} className="relative">
                      <img src={p} alt={`upload-${i}`} className="w-full h-20 object-cover rounded border border-slate-200" />
                      <button
                        onClick={() => set("photos", data.photos.filter((_, j) => j !== i))}
                        className="absolute -top-1.5 -right-1.5 bg-slate-900 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
                        data-testid={`remove-photo-${i}`}
                      >×</button>
                    </div>
                  ))}
                </div>
              )}
            </StepWrap>
          )}

          {step === 5 && (
            <StepWrap icon={<User className="w-5 h-5" />} title="Your contact info" subtitle="We&apos;ll send your bid here and confirm your appointment.">
              <Input label="Full name" value={data.full_name} onChange={(v) => set("full_name", v)} placeholder="Jane Homeowner" testId="name-input" />
              <div className="grid md:grid-cols-2 gap-4 mt-4">
                <Input label="Email" type="email" value={data.email} onChange={(v) => set("email", v)} placeholder="jane@example.com" testId="email-input" />
                <Input label="Phone" type="tel" value={data.phone} onChange={(v) => set("phone", v)} placeholder="(817) 555-0123" testId="phone-input" />
              </div>
              <p className="text-xs text-slate-500 mt-4">By submitting, you agree to be contacted about your roofing project. We don't sell your info.</p>
            </StepWrap>
          )}

          {step === 6 && (
            <div className="text-center py-8" data-testid="estimate-step">
              {loadingEst ? (
                <div>
                  <Sparkles className="w-8 h-8 text-amber-500 mx-auto animate-pulse" />
                  <div className="font-display text-2xl mt-4 font-semibold">Calculating your bid…</div>
                  <p className="text-slate-500 text-sm mt-2">Crunching DFW labor & material rates with your details.</p>
                  <div className="tracing mt-6 max-w-sm mx-auto" />
                </div>
              ) : lead ? (
                lead.service_type === 'solar_repair' || (lead.ai_estimate_low === 0 && lead.ai_estimate_high === 0) ? (
                  <div data-testid="estimate-custom-callback">
                    <div className="w-16 h-16 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center mx-auto">
                      <Sun className="w-8 h-8" />
                    </div>
                    <div className="text-xs uppercase tracking-[0.2em] text-amber-600 font-bold mt-6">Custom Quote</div>
                    <h3 className="font-display text-3xl md:text-4xl font-semibold mt-3 text-slate-900 tracking-tight">
                      We&apos;ll call you directly.
                    </h3>
                    <p className="text-slate-600 mt-4 max-w-lg mx-auto leading-relaxed" data-testid="estimate-summary">{lead.ai_summary}</p>
                    <div className="mt-6 max-w-lg mx-auto bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-xs text-slate-700 text-left" data-testid="estimate-disclaimer">
                      {lead.ai_disclaimer}
                    </div>
                    <p className="text-sm text-slate-500 mt-6">Next: pick an inspection / consultation slot and a TDLR-licensed specialist will reach out to confirm scope.</p>
                  </div>
                ) : (
                  <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-amber-600 font-bold">Your AI Bid</div>
                    <div className="font-display text-4xl md:text-5xl font-semibold mt-3 text-slate-900" data-testid="estimate-range">
                      {Math.round(lead.ai_estimate_low) === Math.round(lead.ai_estimate_high)
                        ? `$${Math.round(lead.ai_estimate_low).toLocaleString()}`
                        : `$${Math.round(lead.ai_estimate_low).toLocaleString()} – $${Math.round(lead.ai_estimate_high).toLocaleString()}`}
                    </div>
                    <p className="text-slate-600 mt-4 max-w-lg mx-auto leading-relaxed" data-testid="estimate-summary">{lead.ai_summary}</p>
                    <div className="mt-6 max-w-lg mx-auto bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-xs text-amber-900 text-left" data-testid="estimate-disclaimer">
                      <strong>Disclaimer:</strong> {lead.ai_disclaimer}
                    </div>
                  </div>
                )
              ) : null}
            </div>
          )}

          {step === 7 && (
            <StepWrap icon={<CalIcon className="w-5 h-5" />} title="Schedule your inspection" subtitle="Pick a slot — we&apos;ll confirm via email & text. Free, no obligation.">
              <div className="grid sm:grid-cols-2 gap-6" data-testid="availability-calendar">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Available days</div>
                  <div className="space-y-1.5 max-h-80 overflow-y-auto pr-2">
                    {availability.map((d) => (
                      <button
                        key={d.date}
                        onClick={() => { setSelectedDate(d.date); setSelectedSlot(null); }}
                        disabled={d.available.length === 0}
                        className={`w-full text-left px-4 py-2.5 rounded-lg border text-sm flex justify-between items-center transition ${selectedDate === d.date ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 hover:border-amber-500"} ${d.available.length === 0 ? "opacity-40 cursor-not-allowed" : ""}`}
                        data-testid={`date-${d.date}`}
                      >
                        <span className="font-medium">{new Date(d.date + 'T12:00:00').toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}</span>
                        <span className={`text-xs ${selectedDate === d.date ? "text-amber-300" : "text-slate-500"}`}>{d.available.length} slots</span>
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Times</div>
                  {!selectedDate ? (
                    <div className="text-sm text-slate-400 px-4 py-8 border border-dashed border-slate-200 rounded-lg text-center">Pick a date first.</div>
                  ) : (
                    <div className="grid grid-cols-2 gap-2">
                      {(availability.find((d) => d.date === selectedDate)?.available || []).map((slot) => (
                        <button
                          key={slot}
                          onClick={() => setSelectedSlot(slot)}
                          className={`px-3 py-2 rounded-lg border text-sm transition ${selectedSlot === slot ? "bg-amber-500 text-white border-amber-500" : "bg-white border-slate-200 hover:border-amber-500"}`}
                          data-testid={`slot-${slot}`}
                        >{formatSlot(slot)}</button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="mt-6 pt-5 border-t border-slate-200 text-center" data-testid="callback-section">
                {callbackDone ? (
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-50 text-emerald-700 text-sm border border-emerald-200" data-testid="callback-confirmation">
                    <CheckCircle2 className="w-4 h-4" /> We&apos;ll call you shortly. You can close this window.
                  </div>
                ) : (
                  <>
                    <p className="text-sm text-slate-500">Prefer not to pick a time?</p>
                    <button
                      onClick={callMeInstead}
                      disabled={callingBack}
                      className="mt-2 inline-flex items-center gap-2 px-5 py-2.5 rounded-lg border border-slate-300 text-sm font-medium text-slate-700 hover:border-amber-500 hover:text-amber-700 transition disabled:opacity-50"
                      data-testid="call-me-instead-button"
                    >
                      <Phone className="w-4 h-4" /> {callingBack ? "Sending…" : "Call me instead"}
                    </button>
                  </>
                )}
              </div>
            </StepWrap>
          )}

          {step === 8 && (
            <div className="text-center py-12" data-testid="success-step">
              <div className="w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-8 h-8" />
              </div>
              <h3 className="font-display text-3xl font-semibold mt-6 tracking-tight">You&apos;re booked.</h3>
              <p className="text-slate-600 mt-3 max-w-md mx-auto">
                Inspection confirmed for <strong>{new Date(lead.appointment_date + 'T12:00:00').toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}</strong> at <strong>{formatSlot(lead.appointment_slot)}</strong>. We&apos;ll send a reminder the day before.
              </p>
              <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-50 text-amber-800 text-sm border border-amber-200">
                {lead.ai_estimate_high > 0
                  ? (Math.round(lead.ai_estimate_low) === Math.round(lead.ai_estimate_high)
                      ? `Your bid: $${Math.round(lead.ai_estimate_low).toLocaleString()}`
                      : `Your bid: $${Math.round(lead.ai_estimate_low).toLocaleString()} – $${Math.round(lead.ai_estimate_high).toLocaleString()}`)
                  : `Custom quote — we'll call to scope`}
              </div>
              <button onClick={onClose} className="btn-primary mt-8" data-testid="done-button">Close</button>
            </div>
          )}
        </div>

        {/* Footer */}
        {step < 8 && (
          <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between bg-slate-50">
            <button onClick={back} disabled={step === 0 || loadingEst} className="px-4 py-2 rounded-lg text-sm text-slate-600 hover:bg-white disabled:opacity-30 flex items-center gap-1.5" data-testid="wizard-back-button">
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
            {step === 6 ? (
              <button onClick={next} disabled={loadingEst || !lead} className="btn-amber text-sm" data-testid="wizard-continue-schedule">
                Continue to schedule <ArrowRight className="inline w-4 h-4 ml-1" />
              </button>
            ) : step === 7 ? (
              <button onClick={book} disabled={!selectedDate || !selectedSlot || booking} className="btn-amber text-sm" data-testid="wizard-book-button">
                {booking ? "Booking…" : "Confirm appointment"}
              </button>
            ) : (
              <button onClick={next} disabled={!canNext()} className="btn-amber text-sm disabled:opacity-50" data-testid="wizard-next-button">
                {step === 5 ? "Get my bid" : "Continue"} <ArrowRight className="inline w-4 h-4 ml-1" />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function StepWrap({ icon, title, subtitle, children }) {
  return (
    <div className="max-w-xl mx-auto">
      <div className="flex items-center gap-2 text-amber-600 text-xs font-bold uppercase tracking-[0.2em] mb-3">
        {icon} <span>{title.split(' ')[0]}</span>
      </div>
      <h3 className="font-display text-2xl md:text-3xl font-semibold tracking-tight">{title}</h3>
      {subtitle && <p className="text-slate-500 mt-2">{subtitle}</p>}
      <div className="mt-6">{children}</div>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", placeholder, testId }) {
  return (
    <div>
      <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1 w-full px-4 py-3 rounded-lg border border-slate-200 focus:ring-2 focus:ring-amber-500 focus:outline-none"
        data-testid={testId}
      />
    </div>
  );
}

function RadioGrid({ label, value, onChange, options, testIdPrefix }) {
  return (
    <div className="mt-5">
      <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</label>
      <div className="flex flex-wrap gap-2 mt-2">
        {options.map((o) => {
          const val = typeof o === "object" ? o.value : o;
          const lbl = typeof o === "object" ? o.label : o;
          const isObj = typeof o === "object";
          return (
            <button
              key={val}
              type="button"
              onClick={() => onChange(val)}
              className={`px-4 py-2 rounded-lg border text-sm ${isObj ? "" : "capitalize"} transition ${value === val ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 hover:border-amber-500"}`}
              data-testid={`${testIdPrefix}-${val}`}
            >{lbl}</button>
          );
        })}
      </div>
    </div>
  );
}

function ToggleCard({ active, onClick, label, testId }) {
  return (
    <button
      onClick={onClick}
      className={`px-5 py-6 rounded-xl border text-left transition ${active ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 hover:border-amber-500"}`}
      data-testid={testId}
    >
      <div className="font-display text-lg font-medium">{label}</div>
    </button>
  );
}

function ServiceChoice({ active, onClick, title, body, testId, highlight }) {
  return (
    <button
      onClick={onClick}
      className={`w-full text-left px-5 py-4 rounded-xl border transition flex items-start gap-3 ${active ? "bg-slate-900 text-white border-slate-900" : highlight ? "bg-amber-50 border-amber-300 hover:border-amber-500" : "bg-white border-slate-200 hover:border-amber-500"}`}
      data-testid={testId}
    >
      <div className="flex-1">
        <div className={`font-display text-lg font-medium ${active ? "text-white" : "text-slate-900"}`}>{title}</div>
        <div className={`text-sm mt-1 leading-relaxed ${active ? "text-slate-300" : "text-slate-600"}`}>{body}</div>
      </div>
    </button>
  );
}

function formatSlot(s) {
  if (!s) return "";
  const [h] = s.split(":");
  const hr = parseInt(h, 10);
  const ampm = hr >= 12 ? "PM" : "AM";
  const dh = ((hr + 11) % 12) + 1;
  return `${dh}:00 ${ampm}`;
}
